/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { X, Download, Send, Loader2, CheckCircle2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { brochureApi } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * Brochure download modal — dual CTA:
 *   1. Direct download (opens PDF in new tab)
 *   2. Send by email (captures a lead)
 *
 * Controlled component: parent manages `open` and `onClose`.
 */
export default function BrochureModal({ open, onClose }) {
  const [tab, setTab] = useState("email"); // "email" | "download"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Merci de renseigner un email valide.");
      return;
    }
    if (!consent) {
      toast.error("Merci d'accepter de recevoir la brochure.");
      return;
    }
    setSending(true);
    try {
      await brochureApi.sendByEmail({ email, name, consent });
      setSent(true);
      // GA4 — après réponse serveur OK. Aucune donnée personnelle envoyée.
      try { trackEvent("download_brochure", { method: "email", has_email: true }); } catch (_) {}
      toast.success("Brochure envoyée. Vérifiez votre boîte email.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec de l'envoi. Réessayez plus tard.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const directDownload = () => {
    try { trackEvent("download_brochure", { method: "direct", has_email: false }); } catch (_) {}
    window.open(brochureApi.pdfUrl(), "_blank");
  };

  const close = () => {
    setSent(false);
    setEmail("");
    setName("");
    onClose?.();
  };

  return (
    <div
      data-testid="brochure-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl"
      >
        {/* Header */}
        <div className="relative p-8 pb-6 border-b border-[#DCB457]/15">
          <button
            onClick={close}
            data-testid="brochure-modal-close"
            className="absolute top-4 right-4 text-white/50 hover:text-[#DCB457]"
            aria-label="Fermer"
          >
            <X size={22} strokeWidth={1.4} />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 flex items-center justify-center bg-[#DCB457]/10 border border-[#DCB457]/30 rounded-sm">
              <BookOpen size={18} strokeWidth={1.4} className="text-[#DCB457]" />
            </div>
            <p className="eyebrow text-[10px]">Brochure Premium · 2026</p>
          </div>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mb-2">
            Recevez notre <span className="italic gold-text">brochure.</span>
          </h2>
          <p className="text-white/60 text-sm">
            Toutes nos formules 2026 — mariage, anniversaire, baptême, entreprise,
            résidences, photobooth, blind test et chanteur live. Avec les tarifs.
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 border-b border-[#DCB457]/15">
          <button
            data-testid="brochure-tab-email"
            onClick={() => setTab("email")}
            className={`py-3.5 text-[11px] uppercase tracking-[0.2em] transition ${
              tab === "email" ? "text-[#DCB457] border-b-2 border-[#DCB457] -mb-px" : "text-white/50 hover:text-white/80"
            }`}
          >
            Recevoir par email
          </button>
          <button
            data-testid="brochure-tab-download"
            onClick={() => setTab("download")}
            className={`py-3.5 text-[11px] uppercase tracking-[0.2em] transition ${
              tab === "download" ? "text-[#DCB457] border-b-2 border-[#DCB457] -mb-px" : "text-white/50 hover:text-white/80"
            }`}
          >
            Télécharger
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {tab === "email" ? (
            sent ? (
              <div className="text-center py-6" data-testid="brochure-sent">
                <CheckCircle2 size={44} strokeWidth={1.2} className="text-emerald-400 mx-auto mb-4" />
                <h3 className="font-serif-luxe text-xl mb-2">Brochure envoyée</h3>
                <p className="text-white/60 text-sm mb-6">
                  Vérifiez votre boîte de réception (et vos spams).
                </p>
                <button
                  onClick={close}
                  data-testid="brochure-sent-close"
                  className="btn-ghost-gold"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={submitEmail} className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1.5">Votre email *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="brochure-email-input"
                    placeholder="vous@email.com"
                    className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm text-white focus:border-[#DCB457] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1.5">Votre prénom (facultatif)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="brochure-name-input"
                    placeholder="Prénom"
                    className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm text-white focus:border-[#DCB457] outline-none"
                  />
                </div>
                <label className="flex items-start gap-2.5 text-xs text-white/55 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="brochure-consent"
                    className="mt-0.5 accent-[#DCB457]"
                  />
                  J'accepte de recevoir la brochure par email. Aucun spam, un email unique.
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="brochure-send-btn"
                  className="btn-gold w-full !py-3.5 inline-flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                  ) : (
                    <><Send size={14} strokeWidth={1.5} /> Recevoir la brochure</>
                  )}
                </button>
              </form>
            )
          ) : (
            <div className="space-y-5 py-2">
              <p className="text-white/70 text-sm leading-relaxed">
                Téléchargez la brochure PDF directement, sans laisser votre email.
                Format A4 haute qualité, prêt à imprimer.
              </p>
              <button
                onClick={directDownload}
                data-testid="brochure-download-btn"
                className="btn-gold w-full !py-3.5 inline-flex items-center justify-center gap-2"
              >
                <Download size={14} strokeWidth={1.5} /> Télécharger le PDF
              </button>
              <p className="text-[11px] text-white/40 text-center italic">
                Fichier : Brochure-MyAnimation-2026.pdf
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
